import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import './AdminOrganizationDetail.css';

// Tabs valides (hors du composant pour eviter les re-renders)
const VALID_TABS = ['overview', 'users', 'billing', 'usage', 'contract'];

function AdminOrganizationDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isSuperAdmin } = useAdminAuth();

    const getTabFromHash = () => {
        const hash = location.hash.replace('#', '');
        return VALID_TABS.includes(hash) ? hash : 'overview';
    };

    const [organization, setOrganization] = useState(null);
    const [stats, setStats] = useState(null);
    const [members, setMembers] = useState([]);
    const [billing, setBilling] = useState(null);
    const [usage, setUsage] = useState(null);
    const [currentUsage, setCurrentUsage] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(getTabFromHash);
    const [actionLoading, setActionLoading] = useState(false);
    const [editingContract, setEditingContract] = useState(false);
    const [contractForm, setContractForm] = useState({
        subscription_plan: '',
        max_users: '',
        max_signatures: '',
        max_directory_users: '',
        unit_price_per_month: ''
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [orgRes, usageRes, invoicesRes] = await Promise.all([
                adminApi.getOrganization(id),
                adminApi.getOrganizationUsage(id),
                adminApi.getOrganizationInvoices(id)
            ]);
            // Le backend retourne: { organization, stats, members, billing, invoices, usage }
            setOrganization(orgRes.data.organization);
            setStats(orgRes.data.stats);
            setMembers(orgRes.data.members || []);
            setBilling(orgRes.data.billing);
            setCurrentUsage(orgRes.data.usage);
            setUsage(usageRes.data);
            setInvoices(invoicesRes.data.invoices || orgRes.data.invoices || []);
        } catch (err) {
            setError('Erreur lors du chargement des donnees');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Synchroniser le hash avec l'onglet actif
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (VALID_TABS.includes(hash) && hash !== activeTab) {
            setActiveTab(hash);
        }
    }, [location.hash, activeTab]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        navigate(`#${tab}`, { replace: true });
    };

    const handleToggleStatus = async () => {
        if (!window.confirm(`Voulez-vous vraiment ${organization.is_active ? 'desactiver' : 'activer'} cette organisation ?`)) {
            return;
        }
        setActionLoading(true);
        try {
            await adminApi.toggleOrganizationStatus(id);
            await loadData();
        } catch (err) {
            alert('Erreur lors de la modification du statut');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('ATTENTION: Cette action est irreversible. Voulez-vous vraiment supprimer cette organisation ?')) {
            return;
        }
        if (!window.confirm('Derniere confirmation: toutes les donnees seront perdues. Continuer ?')) {
            return;
        }
        setActionLoading(true);
        try {
            await adminApi.deleteOrganization(id);
            navigate('/admin/organizations');
        } catch (err) {
            alert('Erreur lors de la suppression');
            setActionLoading(false);
        }
    };

    const startEditContract = () => {
        setContractForm({
            subscription_plan: organization.subscription_plan || 'free',
            max_users: organization.max_users === -1 ? '-1' : (organization.max_users || ''),
            max_signatures: organization.max_signatures === -1 ? '-1' : (organization.max_signatures || ''),
            max_directory_users: organization.max_directory_users === -1 ? '-1' : (organization.max_directory_users || ''),
            unit_price_per_month: organization.unit_price_per_month || '0'
        });
        setEditingContract(true);
    };

    const handleContractChange = (e) => {
        const { name, value } = e.target;
        setContractForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveContract = async () => {
        setActionLoading(true);
        try {
            await adminApi.updateOrganization(id, {
                subscription_plan: contractForm.subscription_plan,
                max_users: contractForm.max_users === '-1' ? -1 : parseInt(contractForm.max_users) || 0,
                max_signatures: contractForm.max_signatures === '-1' ? -1 : parseInt(contractForm.max_signatures) || 0,
                max_directory_users: contractForm.max_directory_users === '-1' ? -1 : parseInt(contractForm.max_directory_users) || 0,
                unit_price_per_month: parseFloat(contractForm.unit_price_per_month) || 0
            });
            await loadData();
            setEditingContract(false);
        } catch (err) {
            alert('Erreur lors de la sauvegarde');
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggleUserStatus = async (userId, currentStatus) => {
        const action = currentStatus ? 'desactiver' : 'activer';
        if (!window.confirm(`Voulez-vous vraiment ${action} cet utilisateur ?`)) {
            return;
        }
        setActionLoading(true);
        try {
            await adminApi.toggleUserStatus(userId);
            // Mettre a jour l'etat local
            setMembers(prev => prev.map(user =>
                user.id === userId
                    ? { ...user, is_active: !user.is_active }
                    : user
            ));
        } catch (err) {
            alert('Erreur lors du changement de statut');
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const handleInvoiceStatusChange = async (invoiceId, newStatus) => {
        const statusLabels = {
            'paid': 'payee',
            'cancelled': 'annulee',
            'refunded': 'remboursee'
        };
        if (!window.confirm(`Marquer cette facture comme ${statusLabels[newStatus] || newStatus} ?`)) {
            return;
        }
        setActionLoading(true);
        try {
            await adminApi.updateInvoiceStatus(invoiceId, newStatus);
            // Mettre a jour l'etat local
            setInvoices(prev => prev.map(inv =>
                inv.id === invoiceId
                    ? { ...inv, status: newStatus }
                    : inv
            ));
        } catch (err) {
            alert('Erreur lors du changement de statut');
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    const getInvoiceStatusLabel = (status) => {
        const labels = {
            'draft': 'Brouillon',
            'sent': 'Envoyee',
            'paid': 'Payee',
            'cancelled': 'Annulee',
            'refunded': 'Remboursee'
        };
        return labels[status] || status;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('fr-FR');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount / 100);
    };

    const getPlanLabel = (plan) => {
        const plans = {
            'free': 'Gratuit',
            'pro': 'Pro',
            'enterprise': 'Entreprise'
        };
        return plans[plan] || plan;
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="admin-loading-inline">Chargement...</div>
            </AdminLayout>
        );
    }

    if (error || !organization) {
        return (
            <AdminLayout>
                <div className="admin-error">{error || 'Organisation non trouvee'}</div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-org-detail">
                {/* Header */}
                <div className="admin-page-header with-actions">
                    <div>
                        <div className="breadcrumb">
                            <Link to="/admin/organizations">Organisations</Link>
                            <span>/</span>
                            <span>{organization.name}</span>
                        </div>
                        <h1>{organization.name}</h1>
                        <p>Creee le {formatDate(organization.created_at)}</p>
                    </div>
                    <div className="header-actions">
                        <button
                            className={`admin-btn ${organization.is_active ? 'admin-btn-warning' : 'admin-btn-success'}`}
                            onClick={handleToggleStatus}
                            disabled={actionLoading}
                        >
                            {organization.is_active ? 'Desactiver' : 'Activer'}
                        </button>
                        {isSuperAdmin() && (
                            <button
                                className="admin-btn admin-btn-danger"
                                onClick={handleDelete}
                                disabled={actionLoading}
                            >
                                Supprimer
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div className="admin-tabs">
                    <button
                        className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => handleTabChange('overview')}
                    >
                        Vue d'ensemble
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => handleTabChange('users')}
                    >
                        Utilisateurs ({members.length || stats?.users_count || 0})
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'billing' ? 'active' : ''}`}
                        onClick={() => handleTabChange('billing')}
                    >
                        Facturation
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'usage' ? 'active' : ''}`}
                        onClick={() => handleTabChange('usage')}
                    >
                        Usage
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'contract' ? 'active' : ''}`}
                        onClick={() => handleTabChange('contract')}
                    >
                        Contrat
                    </button>
                </div>

                {/* Tab Content */}
                <div className="admin-tab-content">
                    {activeTab === 'overview' && (
                        <div className="overview-grid">
                            {/* Status Card */}
                            <div className="admin-card">
                                <h3 className="admin-card-title">Statut</h3>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Etat</span>
                                        <span className={`admin-status ${organization.is_active ? 'active' : 'inactive'}`}>
                                            {organization.is_active ? 'Actif' : 'Inactif'}
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Plan</span>
                                        <span className={`plan-badge ${organization.subscription_plan || 'free'}`}>
                                            {getPlanLabel(organization.subscription_plan)}
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Periode</span>
                                        <span className="info-value">
                                            {organization.billing_period === 'yearly' ? 'Annuel' : 'Mensuel'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Stats Card */}
                            <div className="admin-card">
                                <h3 className="admin-card-title">Statistiques</h3>
                                <div className="stats-row">
                                    <div className="stat-box">
                                        <span className="stat-number">{stats?.directory_users_count || 0}</span>
                                        <span className="stat-label">Membres annuaire</span>
                                    </div>
                                    <div className="stat-box">
                                        <span className="stat-number">{currentUsage?.operators_count || stats?.operators_count || 0}</span>
                                        <span className="stat-label">Operateurs</span>
                                    </div>
                                    <div className="stat-box">
                                        <span className="stat-number">{stats?.signatures_count || 0}</span>
                                        <span className="stat-label">Signatures</span>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Card */}
                            <div className="admin-card full-width">
                                <h3 className="admin-card-title">Informations de contact</h3>
                                <div className="info-grid wide">
                                    <div className="info-item">
                                        <span className="info-label">Email facturation</span>
                                        <span className="info-value">{billing?.email || organization.billing_email || '-'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Adresse</span>
                                        <span className="info-value">
                                            {billing?.address || organization.billing_address || '-'}
                                            {(billing?.city || organization.billing_city) && `, ${billing?.city || organization.billing_city}`}
                                            {(billing?.postal_code || organization.billing_postal_code) && ` ${billing?.postal_code || organization.billing_postal_code}`}
                                            {(billing?.country || organization.billing_country) && `, ${billing?.country || organization.billing_country}`}
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Numero TVA</span>
                                        <span className="info-value">{billing?.vat_number || organization.billing_vat_number || '-'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">SIRET</span>
                                        <span className="info-value">{billing?.siret || organization.billing_siret || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className="admin-card">
                            {members.length > 0 ? (
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Nom</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Derniere connexion</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.map(user => (
                                            <tr key={user.id}>
                                                <td>{user.first_name} {user.last_name}</td>
                                                <td>{user.email}</td>
                                                <td>
                                                    <span className={`role-badge ${user.role_name || 'user'}`}>
                                                        {user.role_label || (user.role_name === 'admin' ? 'Admin' : 'Utilisateur')}
                                                    </span>
                                                </td>
                                                <td>{formatDateTime(user.last_login)}</td>
                                                <td>
                                                    <span className={`admin-status ${user.is_active ? 'active' : 'inactive'}`}>
                                                        {user.is_active ? 'Actif' : 'Inactif'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className={`admin-btn admin-btn-sm ${user.is_active ? 'admin-btn-warning' : 'admin-btn-success'}`}
                                                        onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                                                        disabled={actionLoading}
                                                    >
                                                        {user.is_active ? 'Desactiver' : 'Activer'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="no-data">Aucun utilisateur</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'billing' && (
                        <div className="billing-content">
                            {/* Billing Info */}
                            <div className="admin-card">
                                <h3 className="admin-card-title">Informations de facturation</h3>
                                <div className="info-grid wide">
                                    <div className="info-item">
                                        <span className="info-label">Raison sociale</span>
                                        <span className="info-value">{billing?.company_name || organization.billing_company_name || organization.name}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Email</span>
                                        <span className="info-value">{billing?.email || organization.billing_email || '-'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Client Stripe</span>
                                        <span className="info-value">{organization.stripe_customer_id || 'Non connecte'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Abonnement Stripe</span>
                                        <span className="info-value">{organization.stripe_subscription_id || 'Aucun'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Prix unitaire</span>
                                        <span className="info-value">{billing?.unit_price ? `${billing.unit_price} € / mois` : '-'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Plan</span>
                                        <span className="info-value">{getPlanLabel(organization.subscription_plan)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Invoices */}
                            <div className="admin-card">
                                <h3 className="admin-card-title">Factures</h3>
                                {invoices.length > 0 ? (
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>Numero</th>
                                                <th>Date</th>
                                                <th>Montant</th>
                                                <th>Statut</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map(invoice => (
                                                <tr key={invoice.id}>
                                                    <td>{invoice.invoice_number || invoice.number}</td>
                                                    <td>{formatDate(invoice.invoice_date || invoice.date)}</td>
                                                    <td>{invoice.total_ttc ? `${parseFloat(invoice.total_ttc).toFixed(2)} EUR` : formatCurrency(invoice.amount)}</td>
                                                    <td>
                                                        <span className={`admin-status ${invoice.status}`}>
                                                            {getInvoiceStatusLabel(invoice.status)}
                                                        </span>
                                                    </td>
                                                    <td className="invoice-actions">
                                                        <button
                                                            className="admin-btn admin-btn-secondary admin-btn-sm"
                                                            onClick={() => {
                                                                const token = localStorage.getItem('admin_access_token');
                                                                window.open(
                                                                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${invoice.id}/pdf?token=${token}`,
                                                                    '_blank'
                                                                );
                                                            }}
                                                        >
                                                            PDF
                                                        </button>
                                                        {invoice.status === 'sent' && (
                                                            <button
                                                                className="admin-btn admin-btn-success admin-btn-sm"
                                                                onClick={() => handleInvoiceStatusChange(invoice.id, 'paid')}
                                                                disabled={actionLoading}
                                                            >
                                                                Payee
                                                            </button>
                                                        )}
                                                        {(invoice.status === 'sent' || invoice.status === 'draft') && (
                                                            <button
                                                                className="admin-btn admin-btn-danger admin-btn-sm"
                                                                onClick={() => handleInvoiceStatusChange(invoice.id, 'cancelled')}
                                                                disabled={actionLoading}
                                                            >
                                                                Annuler
                                                            </button>
                                                        )}
                                                        {invoice.status === 'paid' && (
                                                            <button
                                                                className="admin-btn admin-btn-warning admin-btn-sm"
                                                                onClick={() => handleInvoiceStatusChange(invoice.id, 'refunded')}
                                                                disabled={actionLoading}
                                                            >
                                                                Rembourser
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p className="no-data">Aucune facture</p>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'usage' && (
                        <div className="usage-content">
                            {/* Current Stats */}
                            <div className="admin-card">
                                <h3 className="admin-card-title">Utilisation actuelle</h3>
                                <div className="usage-stats">
                                    <div className="usage-stat">
                                        <div className="usage-header">
                                            <span className="usage-label">Signatures</span>
                                            <span className="usage-count">
                                                {stats?.signatures_count || 0} / {organization.max_signatures === -1 ? 'Illimite' : organization.max_signatures || 'Illimite'}
                                            </span>
                                        </div>
                                        <div className="usage-bar">
                                            <div
                                                className="usage-fill"
                                                style={{
                                                    width: !organization.max_signatures || organization.max_signatures === -1 ? '0%' :
                                                           `${Math.min(100, ((stats?.signatures_count || 0) / organization.max_signatures) * 100)}%`
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="usage-stat">
                                        <div className="usage-header">
                                            <span className="usage-label">Utilisateurs</span>
                                            <span className="usage-count">
                                                {stats?.users_count || 0} / {organization.max_users === -1 ? 'Illimite' : organization.max_users || 'Illimite'}
                                            </span>
                                        </div>
                                        <div className="usage-bar">
                                            <div
                                                className="usage-fill"
                                                style={{
                                                    width: !organization.max_users || organization.max_users === -1 ? '0%' :
                                                           `${Math.min(100, ((stats?.users_count || 0) / organization.max_users) * 100)}%`
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="usage-stat">
                                        <div className="usage-header">
                                            <span className="usage-label">Templates</span>
                                            <span className="usage-count">
                                                {stats?.templates_count || 0}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="usage-stat">
                                        <div className="usage-header">
                                            <span className="usage-label">Membres annuaire</span>
                                            <span className="usage-count">
                                                {stats?.directory_users_count || 0} / {organization.max_directory_users === -1 ? 'Illimite' : organization.max_directory_users || 'Illimite'}
                                            </span>
                                        </div>
                                        <div className="usage-bar">
                                            <div
                                                className="usage-fill"
                                                style={{
                                                    width: !organization.max_directory_users || organization.max_directory_users === -1 ? '0%' :
                                                           `${Math.min(100, ((stats?.directory_users_count || 0) / organization.max_directory_users) * 100)}%`
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Billing Periods */}
                            {usage?.billing_periods?.length > 0 && (
                                <div className="admin-card">
                                    <h3 className="admin-card-title">Historique de facturation</h3>
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>Periode</th>
                                                <th>Utilisateurs</th>
                                                <th>Montant HT</th>
                                                <th>Statut</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {usage.billing_periods.map((period, idx) => (
                                                <tr key={idx}>
                                                    <td>{period.period_month}/{period.period_year}</td>
                                                    <td>{period.total_billable_units || 0}</td>
                                                    <td>{period.total_amount_ht ? `${parseFloat(period.total_amount_ht).toFixed(2)} €` : '-'}</td>
                                                    <td>
                                                        <span className={`admin-status ${period.invoice_id ? 'active' : 'pending'}`}>
                                                            {period.invoice_id ? 'Facturé' : 'En cours'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'contract' && (
                        <div className="contract-content">
                            <div className="admin-card">
                                <div className="admin-card-header">
                                    <h3 className="admin-card-title">Parametres du contrat</h3>
                                    {!editingContract && (
                                        <button
                                            className="admin-btn admin-btn-primary"
                                            onClick={startEditContract}
                                        >
                                            Modifier
                                        </button>
                                    )}
                                </div>

                                {editingContract ? (
                                    <div className="contract-form">
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Plan d'abonnement</label>
                                                <select
                                                    name="subscription_plan"
                                                    value={contractForm.subscription_plan}
                                                    onChange={handleContractChange}
                                                >
                                                    <option value="free">Gratuit (Essai)</option>
                                                    <option value="pro">Pro</option>
                                                    <option value="enterprise">Entreprise</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Prix unitaire (EUR / mois)</label>
                                                <input
                                                    type="number"
                                                    name="unit_price_per_month"
                                                    value={contractForm.unit_price_per_month}
                                                    onChange={handleContractChange}
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0.00"
                                                />
                                                <span className="form-hint">Prix par utilisateur facturable</span>
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Limite d'utilisateurs</label>
                                                <input
                                                    type="number"
                                                    name="max_users"
                                                    value={contractForm.max_users}
                                                    onChange={handleContractChange}
                                                    min="-1"
                                                    placeholder="10"
                                                />
                                                <span className="form-hint">-1 pour illimite</span>
                                            </div>
                                            <div className="form-group">
                                                <label>Limite de signatures</label>
                                                <input
                                                    type="number"
                                                    name="max_signatures"
                                                    value={contractForm.max_signatures}
                                                    onChange={handleContractChange}
                                                    min="-1"
                                                    placeholder="10"
                                                />
                                                <span className="form-hint">-1 pour illimite</span>
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Limite membres annuaire</label>
                                                <input
                                                    type="number"
                                                    name="max_directory_users"
                                                    value={contractForm.max_directory_users}
                                                    onChange={handleContractChange}
                                                    min="-1"
                                                    placeholder="500"
                                                />
                                                <span className="form-hint">Nombre max d'adresses email autorisees (-1 pour illimite)</span>
                                            </div>
                                        </div>
                                        <div className="form-actions">
                                            <button
                                                className="admin-btn admin-btn-secondary"
                                                onClick={() => setEditingContract(false)}
                                                disabled={actionLoading}
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                className="admin-btn admin-btn-primary"
                                                onClick={handleSaveContract}
                                                disabled={actionLoading}
                                            >
                                                {actionLoading ? 'Enregistrement...' : 'Enregistrer'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="contract-view">
                                        <div className="info-grid wide">
                                            <div className="info-item">
                                                <span className="info-label">Plan d'abonnement</span>
                                                <span className={`plan-badge ${organization.subscription_plan || 'free'}`}>
                                                    {getPlanLabel(organization.subscription_plan)}
                                                </span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">Prix unitaire</span>
                                                <span className="info-value highlight">
                                                    {parseFloat(organization.unit_price_per_month || 0).toFixed(2)} EUR / mois
                                                </span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">Limite d'utilisateurs</span>
                                                <span className="info-value">
                                                    {organization.max_users === -1 ? 'Illimite' : organization.max_users || 0}
                                                </span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">Limite de signatures</span>
                                                <span className="info-value">
                                                    {organization.max_signatures === -1 ? 'Illimite' : organization.max_signatures || 0}
                                                </span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">Limite membres annuaire</span>
                                                <span className="info-value">
                                                    {organization.max_directory_users === -1 ? 'Illimite' : organization.max_directory_users || 0}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="contract-summary">
                                            <h4>Estimation mensuelle</h4>
                                            <div className="summary-grid">
                                                <div className="summary-item">
                                                    <span className="summary-label">Utilisateurs facturables</span>
                                                    <span className="summary-value">{currentUsage?.total_billable_units || 0}</span>
                                                </div>
                                                <div className="summary-item">
                                                    <span className="summary-label">Prix unitaire</span>
                                                    <span className="summary-value">{parseFloat(organization.unit_price_per_month || 0).toFixed(2)} EUR</span>
                                                </div>
                                                <div className="summary-item total">
                                                    <span className="summary-label">Total estime HT</span>
                                                    <span className="summary-value">
                                                        {((currentUsage?.total_billable_units || 0) * parseFloat(organization.unit_price_per_month || 0)).toFixed(2)} EUR
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminOrganizationDetail;
